/* eslint-disable react-refresh/only-export-components */

import {
  Engineering as EngineeringIcon,
  People as PeopleIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { formatDateTime } from "@repo/utils/date";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import type { FindAllOrganizationsQuery } from "@/libs/generated/operations";
import { RoutePaths } from "@/utils/constants/routes";

type OrganizationRecord = FindAllOrganizationsQuery["findAllOrganizations"]["records"][0];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isAdmin() {
  return typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
}

// ─── Shared Cell Components ───────────────────────────────────────────────────

function ActionsCell({ row }: { row: { original: OrganizationRecord } }) {
  const { t } = useTranslation();
  const { slug, name } = row.original;

  const getButtonSx = (color: string) => ({
    border: 1,
    borderColor: "divider",
    boxShadow: 1,
    "&:hover": { boxShadow: 2 },
    color: `${color}.main`,
  });

  const basePath = isAdmin() ? RoutePaths.adminOrganizationsTable : "";

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", justifyContent: "center" }}>
      <Tooltip title={t("delivery.action.details")}>
        <IconButton
          component={Link}
          to={`${basePath}/${slug}`}
          size="small"
          sx={getButtonSx("primary")}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("layouts.sub-headers.organization.members")}>
        <IconButton
          component={Link}
          to={`${basePath}/view-members/${name}/${slug}`}
          size="small"
          sx={getButtonSx("info")}
        >
          <PeopleIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("layouts.sub-headers.organization.managers")}>
        <IconButton
          component={Link}
          to={`${basePath}/view-managers/${name}/${slug}`}
          size="small"
          sx={getButtonSx("warning")}
        >
          <EngineeringIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

// ─── Table Columns ───────────────────────────────────────────────────────────────

export const ORGANIZATION_TABLE_COLUMNS: ColumnDef<OrganizationRecord>[] = [
  {
    accessorKey: "name",
    enableColumnFilter: true,
    meta: {
      variant: "text",
      label: "Organisation",
      placeholder: "Search by organization",
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Organisation" />,
    cell: ({ row }) => (
      <div>
        <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>{row.original.name}</div>
        <div style={{ fontSize: "0.75rem", color: "#666" }}>{row.original.slug}</div>
      </div>
    ),
    enableSorting: true,
  },
  {
    accessorKey: "createdAt",
    meta: { label: "Date de création" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date de création" />,
    cell: ({ row }) => (
      <Typography variant="body2">
        {formatDateTime(row.original.createdAt, { fallback: "-" })}
      </Typography>
    ),
    enableSorting: true,
  },
  {
    id: "owner",
    accessorFn: (row) => row.owner?.assigneeFirstName ?? "",
    meta: { label: "Propriétaire" },
    header: () => <Typography variant="subtitle1">Propriétaire</Typography>,
    cell: ({ row }) => (
      <div>
        <div style={{ fontWeight: 500, fontSize: "0.875rem" }}>
          {row.original.owner?.assigneeFirstName ?? "-"}
        </div>
        <div style={{ fontSize: "0.75rem", color: "#666" }}>
          {row.original.owner?.assigneePhone ?? "-"}
        </div>
      </div>
    ),
    enableSorting: false,
  },
  {
    accessorKey: "members_count",
    meta: { label: "Membres" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="total-members" />,
    cell: ({ row }) => (
      <Chip label={row.original.members_count} size="small" color="primary" variant="outlined" />
    ),
    enableSorting: true,
  },
  {
    accessorKey: "deliveries_count",
    meta: { label: "Livraisons" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="total-deliveries" />,
    cell: ({ row }) => (
      <Chip
        label={row.original.deliveries_count}
        size="small"
        color="secondary"
        variant="outlined"
      />
    ),
    enableSorting: true,
  },
  {
    id: "actions",
    meta: { label: "Action" },
    header: () => <Typography variant="subtitle1">Action</Typography>,
    cell: ({ row }) => <ActionsCell row={row} />,
    enableSorting: false,
    enableHiding: false,
  },
];
